/**
 * DictBlocks — блоки потребителей карточек Справочников (screen-21, DICT-3).
 * ConsumersBlock — cblk эталона (J:165-195): хедер «{title} (N)» + шеврон,
 * «+» cadd → Dropdown single словаря (добавленные disabled) → NumPadPop int
 * «{имя} — норма» (value 1, max 99); строка crow: имя, qty-кнопка → NumPadPop,
 * единица, суффикс cper с циклами эталона (pl↔ga, tea↔gu, sh→dd «/N дн»:
 * тап по числу — период, мимо числа — назад в sh; hot = pl,gu,sh) и ✕.
 * BindBlock — rblk (J:259-276): привязки без норм, «+» title «Привязать» →
 * Dropdown с multi (тап = один, long-press = мультивыбор), строки имя + ✕.
 * Wire-кодировка per — по-таблично (Д11), конверсия UI↔wire у карточек.
 */
import { useState, type ReactNode } from "react";
import { Dropdown } from "../../components/shared";
import { CrossIco } from "./questIcons";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import css from "./dicts.module.css";

/** UI-коды нормы (эталон PERLAB/PERHOT/PERTOG J:60-62). */
/* 05.10: hr/dy — общий расход филиала «на час работы» / «на рабочий день». */
export type Per = "pl" | "ga" | "gu" | "tea" | "sh" | "dd" | "hr" | "dy";
/** 10.09 (владелец): round — округление расхода вверх до кратного, своё у каждой
 *  привязки: «в одной чайной по одной формуле, в другой по другой». 0 = без округления. */
export interface ConsUiRow { id: string; name: string; qty: number; per: Per; days?: number; round?: number }
/** Конфиг общего NumPadPop карточки (лекало PopCfg PriceRow, max опционален). */
export interface DictPop {
  anchor: HTMLElement;
  label: string;
  mode: "money" | "int";
  unit?: string;
  value: number;
  max?: number;
  /** 31.08 (владелец): дробная цена расходника — клавиша «.» в паде. */
  decimals?: number;
  onCommit: (v: number) => void;
}

const PERLAB: Record<string, string> = {
  pl: "/игрок", ga: "/игра", gu: "/гость", tea: "/чаепитие", sh: "/смена", hr: "/час работы", dy: "/день работы",
};
const PERHOT: Record<string, 1> = { pl: 1, gu: 1, sh: 1, hr: 1 };
const PERTOG: Record<string, Per> = { pl: "ga", ga: "pl", gu: "tea", tea: "gu", hr: "dy", dy: "hr" };

/** Хедер блока: кнопка-заголовок с шевроном + «+» (stopPropagation — J:171). */
function Head(p: {
  title: string; count: number; open: boolean; onToggle: () => void;
  addTitle: string; onAdd: (anchor: HTMLElement) => void;
}) {
  return (
    <div className={css.conshd}>
      <button type="button" className={css.conshdT} onClick={p.onToggle}>
        <span className={cfg.klbl}>{p.title} ({p.count})</span>
        <span className={`${css.chev} ${p.open ? css.up : ""}`}>▾</span>
      </button>
      <button type="button" className={css.cadd} title={p.addTitle}
        onClick={(e) => { e.stopPropagation(); p.onAdd(e.currentTarget); }}>
        +
      </button>
    </div>
  );
}

/** Суффикс-циклы нормы (J:184-191): dd — «/N дн», число с NumPadPop-периодом. */
function PerBtn(p: {
  row: ConsUiRow;
  onPatch: (patch: Partial<ConsUiRow>) => void;
  openPop: (cfg: DictPop) => void;
}) {
  const r = p.row;
  if (r.per === "dd") {
    return (
      <button type="button" className={`${css.cper} ${css.dim}`}
        onClick={() => p.onPatch({ per: "sh", days: undefined })}>
        /<span
          className={css.dnum}
          onClick={(e) => {
            e.stopPropagation();
            p.openPop({
              anchor: e.currentTarget, label: `${r.name} — период`, mode: "int",
              unit: "дн", value: r.days || 30, max: 999,
              onCommit: (v) => p.onPatch({ days: v || 30 }),
            });
          }}
        >{r.days || 30}</span> дн
      </button>
    );
  }
  const tap = r.per === "sh"
    ? () => p.onPatch({ per: "dd", days: r.days || 30 })
    : () => p.onPatch({ per: PERTOG[r.per] });
  return (
    <button type="button" className={`${css.cper} ${PERHOT[r.per] ? css.hot : css.dim}`} onClick={tap}>
      {PERLAB[r.per]}
    </button>
  );
}

export interface ConsumersBlockProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  rows: ConsUiRow[];
  /** opened=true после добавления — блок разворачивается (эталон st[key]=true). */
  onChange: (rows: ConsUiRow[], opened?: boolean) => void;
  dict: { id: string; label: string }[];
  perDef: Per;
  unit: string;
  openPop: (cfg: DictPop) => void;
  /** Read-only хвост (нормы спецверсий в блоке Квесты — Д3). */
  extra?: ReactNode;
  extraCount?: number;
  /** 05.10: дробная норма (электричество 2,5 кВт·ч в час) — знаков после запятой и потолок. */
  qtyDec?: number;
  qtyMax?: number;
}

export function ConsumersBlock(p: ConsumersBlockProps) {
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const upd = (i: number, patch: Partial<ConsUiRow>) =>
    p.onChange(p.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className={css.consblk}>
      <Head title={p.title} count={p.rows.length + (p.extraCount ?? 0)} open={p.open}
        onToggle={p.onToggle} addTitle="Добавить" onAdd={setDd} />
      {/* 02.09 (владелец): несколько чайных/квестов сразу — мультивыбор по
          умолчанию; один пункт — норма для него, несколько — одна норма на
          всех выбранных (дальше правится построчно) */}
      <Dropdown
        open={!!dd} anchor={dd} multiDefault
        items={p.dict.map((x) => ({ id: x.id, label: x.label, disabled: p.rows.some((r) => r.id === x.id) }))}
        onClose={() => setDd(null)}
        onCommit={(ids) => {
          const fresh = ids.map(String).filter((id, i, a) => a.indexOf(id) === i && !p.rows.some((r) => r.id === id));
          if (!fresh.length || !dd) return;
          const nameOf = (id: string) => p.dict.find((x) => x.id === id)?.label ?? id;
          const label = fresh.length === 1 ? `${nameOf(fresh[0]!)} — норма` : `Норма для ${fresh.length} выбранных`;
          p.openPop({
            anchor: dd, label, mode: "int", unit: p.unit, value: 1, max: p.qtyMax ?? 99, decimals: p.qtyDec,
            onCommit: (v) => p.onChange([...p.rows, ...fresh.map((id) => ({ id, name: nameOf(id), qty: v || 1, per: p.perDef }))], true),
          });
        }}
      />
      {p.open ? (
        <div className={css.crows}>
          {p.rows.map((r, i) => (
            <div key={r.id} className={css.crow}>
              <span className={css.cn}>{r.name}</span>
              <button type="button" className={`${cfg.teNumbtn} ${qcss.num}`}
                onClick={(e) => p.openPop({
                  anchor: e.currentTarget, label: `${r.name} — норма`, mode: "int",
                  unit: p.unit, value: r.qty, max: p.qtyMax ?? 99, decimals: p.qtyDec,
                  onCommit: (v) => upd(i, { qty: v || 1 }),
                })}>
                {String(r.qty).replace(".", ",")}
              </button>
              <span className={css.cu}>{p.unit}</span>
              <button type="button" className={`${css.cround} ${r.round ? css.hot : css.dim}`}
                title="Округление расхода для этой привязки: вверх до кратного. Тап — задать, 0 — без округления"
                onClick={(e) => p.openPop({
                  anchor: e.currentTarget, label: `${r.name} — округлять до кратного`, mode: "int",
                  unit: p.unit, value: r.round ?? 0, max: 999,
                  onCommit: (v) => upd(i, { round: Math.max(0, v || 0) }),
                })}>
                {r.round ? `окр. ${r.round}` : "окр. нет"}
              </button>
              <PerBtn row={r} onPatch={(patch) => upd(i, patch)} openPop={p.openPop} />
              <button type="button" className={qcss.tx}
                onClick={() => p.onChange(p.rows.filter((_, j) => j !== i))}>
                <CrossIco w={12} />
              </button>
            </div>
          ))}
          {p.extra}
        </div>
      ) : null}
    </div>
  );
}

export interface BindRow { id: string; name: string }

/** rblk — привязки без норм (карточка ресурса): multi-Dropdown, имя + ✕. */
export function BindBlock(p: {
  title: string;
  open: boolean;
  onToggle: () => void;
  rows: BindRow[];
  onChange: (rows: BindRow[], opened?: boolean) => void;
  dict: { id: string; label: string }[];
}) {
  const [dd, setDd] = useState<HTMLElement | null>(null);
  return (
    <div className={css.consblk}>
      <Head title={p.title} count={p.rows.length} open={p.open}
        onToggle={p.onToggle} addTitle="Привязать" onAdd={setDd} />
      <Dropdown
        open={!!dd} anchor={dd} multiDefault /* 28.08 (владелец): привязки — сразу мультивыбор */
        items={p.dict.map((x) => ({ id: x.id, label: x.label, disabled: p.rows.some((r) => r.id === x.id) }))}
        onClose={() => setDd(null)}
        onCommit={(ids) => {
          const add = ids.map(String)
            .filter((id, i, a) => a.indexOf(id) === i && !p.rows.some((r) => r.id === id))
            .map((id) => ({ id, name: p.dict.find((x) => x.id === id)?.label ?? id }));
          if (add.length) p.onChange([...p.rows, ...add], true);
        }}
      />
      {p.open ? (
        <div className={css.crows}>
          {p.rows.map((r, i) => (
            <div key={r.id} className={css.crow}>
              <span className={css.cn}>{r.name}</span>
              <button type="button" className={qcss.tx}
                onClick={() => p.onChange(p.rows.filter((_, j) => j !== i))}>
                <CrossIco w={12} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
